/**
 * Input Type Configuration
 * 
 * SAFE CLEANUP: Centralized display configuration for input types.
 * Used for rendering icons, labels, and colors consistently across the app.
 * 
 * Consumers: Command, AgentControlCentre, RunAgentModal, AgentMarketplaceV2
 */

import { ACCEPTED_INPUT_TYPES } from './agentTypes';

/**
 * Display configuration for each input type
 * icon: Emoji or icon identifier
 * label: Human-readable label
 * color: Tailwind color name (without prefix)
 * description: Short description for tooltips
 */
export const INPUT_TYPE_CONFIG = {
  [ACCEPTED_INPUT_TYPES.TEXT]: {
    icon: '💬',
    label: 'Text',
    color: 'blue',
    description: 'Plain text input',
  },
  [ACCEPTED_INPUT_TYPES.FILE]: {
    icon: '📎',
    label: 'File',
    color: 'gray',
    description: 'Generic file upload',
  },
  [ACCEPTED_INPUT_TYPES.FILE_PDF]: {
    icon: '📄',
    label: 'PDF',
    color: 'red',
    description: 'PDF document',
  },
  [ACCEPTED_INPUT_TYPES.FILE_IMAGE]: {
    icon: '🖼️',
    label: 'Image',
    color: 'purple',
    description: 'Image file (PNG, JPG, etc.)',
  },
  [ACCEPTED_INPUT_TYPES.FILE_EXCEL]: {
    icon: '📊',
    label: 'Excel',
    color: 'green',
    description: 'Excel spreadsheet',
  },
  [ACCEPTED_INPUT_TYPES.FILE_DOCUMENT]: {
    icon: '📝',
    label: 'Document',
    color: 'blue',
    description: 'Word document',
  },
  [ACCEPTED_INPUT_TYPES.FILE_VIDEO]: {
    icon: '🎬',
    label: 'Video',
    color: 'pink',
    description: 'Video file',
  },
  [ACCEPTED_INPUT_TYPES.AUDIO]: {
    icon: '🎵',
    label: 'Audio',
    color: 'orange',
    description: 'Audio file',
  },
  [ACCEPTED_INPUT_TYPES.VIDEO]: {
    icon: '🎥',
    label: 'Video',
    color: 'pink',
    description: 'Video file',
  },
  [ACCEPTED_INPUT_TYPES.VOICE]: {
    icon: '🎙️',
    label: 'Voice',
    color: 'purple',
    description: 'Voice/microphone input',
  },
  [ACCEPTED_INPUT_TYPES.URL]: {
    icon: '🔗',
    label: 'URL',
    color: 'cyan',
    description: 'Web URL',
  },
  [ACCEPTED_INPUT_TYPES.JSON]: {
    icon: '{}',
    label: 'JSON',
    color: 'yellow',
    description: 'JSON data',
  },
};

/**
 * Get the icon for an input type
 * @param {string} inputType - The input type
 * @returns {string} Emoji icon
 */
export const getInputIcon = (inputType) => {
  return INPUT_TYPE_CONFIG[inputType]?.icon || '📎';
};

/**
 * Get the label for an input type
 * @param {string} inputType - The input type
 * @returns {string} Human-readable label
 */
export const getInputLabel = (inputType) => {
  return INPUT_TYPE_CONFIG[inputType]?.label || inputType?.replace(/_/g, ' ') || 'Unknown';
};

/**
 * Get the color for an input type
 * @param {string} inputType - The input type
 * @returns {string} Tailwind color name
 */
export const getInputColor = (inputType) => {
  return INPUT_TYPE_CONFIG[inputType]?.color || 'gray';
};

/**
 * Get the full config for an input type
 * @param {string} inputType - The input type
 * @returns {object} Full configuration object
 */
export const getInputConfig = (inputType) => {
  return INPUT_TYPE_CONFIG[inputType] || {
    icon: '📎',
    label: inputType?.replace(/_/g, ' ') || 'Unknown',
    color: 'gray',
    description: 'Unknown input type',
  };
};

/**
 * Input type options for filter dropdowns/checkboxes
 * Used by AgentMarketplaceV2 and similar filter UIs
 */
export const INPUT_TYPE_OPTIONS = [
  { value: 'text', icon: '💬', label: 'Text' },
  { value: 'file_pdf', icon: '📄', label: 'PDF' },
  { value: 'file_image', icon: '🖼️', label: 'Image' },
  { value: 'file_excel', icon: '📊', label: 'Excel' },
  { value: 'file_document', icon: '📝', label: 'Document' },
  { value: 'audio', icon: '🎵', label: 'Audio' },
  { value: 'video', icon: '🎥', label: 'Video' },
  { value: 'voice', icon: '🎙️', label: 'Voice' },
  { value: 'url', icon: '🔗', label: 'URL' },
  { value: 'json', icon: '{}', label: 'JSON' },
];
